import { PHONE_DIALPAD_KEYS } from '@/constants/phoneDialpad';
import { PractitionerResourceOption } from '@/components/PractitionerResourceOption';
import { ActionIcon, Box, Button, Group, Modal, Stack, Text, rem } from '@mantine/core';
import type { ConferenceStartAddTarget } from '@bariendo/lib/twilio-conference-session';
import { createReference, getReferenceString } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { ResourceInput } from '@medplum/react';
import { ReactElement, useEffect, useState } from 'react';
import { IconBackspace, IconX } from '@tabler/icons-react';

export interface AddPersonToCallModalProps {
  opened: boolean;
  onClose: () => void;
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
  onDial: (target: ConferenceStartAddTarget) => void;
}

function phoneFromPractitioner(practitioner: Practitioner | undefined): string | undefined {
  return practitioner?.telecom?.find((t) => t.system === 'phone' && t.value?.trim())?.value;
}

function digitCount(phoneNumber: string): number {
  return phoneNumber.replace(/\D/g, '').length;
}

export function AddPersonToCallModal({
  opened,
  onClose,
  phoneNumber,
  onPhoneNumberChange,
  onDial,
}: AddPersonToCallModalProps): ReactElement {
  const [selectedPractitioner, setSelectedPractitioner] = useState<Practitioner | null>(null);

  useEffect(() => {
    if (!opened) {
      setSelectedPractitioner(null);
    }
  }, [opened]);

  const appendDigit = (digit: string): void => {
    onPhoneNumberChange(phoneNumber + digit);
  };

  const handleDial = (): void => {
    const digits = digitCount(phoneNumber);
    if (digits >= 10) {
      onDial({ type: 'phone', toPhone: phoneNumber.trim() });
      return;
    }
    if (selectedPractitioner) {
      onDial({ type: 'provider', providerReference: getReferenceString(createReference(selectedPractitioner)) });
    }
  };

  const canDial = digitCount(phoneNumber) >= 10 || Boolean(selectedPractitioner);

  return (
    <Modal opened={opened} onClose={onClose} title="Add person to call" centered size="sm">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          The patient will be placed on hold while you speak with the added person. Choose a provider to ring in the
          app, or enter a phone number.
        </Text>
        <div onMouseDown={(e) => e.stopPropagation()}>
          <ResourceInput
            name="provider"
            resourceType="Practitioner"
            onChange={(resource) => {
              const practitioner = (resource as Practitioner) || null;
              setSelectedPractitioner(practitioner);
              const phone = phoneFromPractitioner(practitioner ?? undefined);
              if (phone) {
                onPhoneNumberChange(phone);
              }
            }}
            placeholder="Search practitioner"
            defaultValue={undefined}
            itemComponent={(props: { resource: Practitioner }) => (
              <PractitionerResourceOption resource={props.resource} />
            )}
          />
        </div>
        <Group justify="space-between" align="center" gap="xs" wrap="nowrap">
          <Text
            size="sm"
            c="gray.5"
            style={{
              flex: 1,
              fontFamily: 'monospace',
              letterSpacing: rem(4),
              minHeight: rem(22),
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            aria-live="polite"
          >
            {phoneNumber || ' '}
          </Text>

          <Group gap={4} wrap="nowrap">
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Delete last digit"
              disabled={!phoneNumber}
              onClick={(e) => {
                e.stopPropagation();
                onPhoneNumberChange(phoneNumber.slice(0, -1));
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <IconBackspace size={16} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Clear phone number"
              disabled={!phoneNumber}
              onClick={(e) => {
                e.stopPropagation();
                onPhoneNumberChange('');
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>
        </Group>
        <Box
          display="grid"
          style={{
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: rem(6),
            textAlign: 'center',
          }}
        >
          {PHONE_DIALPAD_KEYS.map((item) => (
            <Button
              key={item.label}
              variant="subtle"
              size="sm"
              radius="md"
              h={56}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                appendDigit(item.label);
              }}
            >
              <Stack gap={0} align="center" justify="center" w="100%">
                <Text fz="lg" c="green.9">
                  {item.label}
                </Text>
                <Text fz="xs" c="dimmed" lh={1}>
                  {item.sub}
                </Text>
              </Stack>
            </Button>
          ))}
        </Box>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleDial} disabled={!canDial} bg={!canDial ? 'gray.2' : 'green'} color="white">
            Dial
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
